// FileInputGallery.jsx (updated with drag, preview, delete)
import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { arrayMoveImmutable } from "array-move";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { FaTrashAlt } from "react-icons/fa";
import { HiOutlineCloudUpload } from "react-icons/hi";
import { HiArrowDownTray } from "react-icons/hi2";
import { ImSpinner2 } from "react-icons/im";

const imgbbKey = "347525e64775187aed4d03329dbe793a";

const SortableItem = SortableElement(({ image, onDelete }) => (
  <div className="relative border rounded-lg overflow-hidden bg-white shadow">
    {image.url && (() => {
      return (
        <img
          src={typeof image.url === "string" ?
            `${process.env.REACT_APP_API_URL}images/${image.url}` :
            `${process.env.REACT_APP_API_URL}images/${image.url?.url}` || ""}
          alt="Uploaded"
          className="w-full h-32 object-cover"
        />
      );
    })()}

    <input
      type="hidden"
      name="gallery_img"
      value={`${process.env.REACT_APP_API_URL}images/${image.url}`}
      src={`${process.env.REACT_APP_API_URL}images/${image.url}`}
      alt={image._id || ""}
    />
    <button
      onClick={() => onDelete(image)}
      className="absolute top-1 right-1 bg-red-500 text-white w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-600 transition"
      type="button"
    >
      <FaTrashAlt className="w-4 h-4 pointer-events-none" />
    </button>
  </div>
));

const SortableList = SortableContainer(({ images, onDelete }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
    {images.map((image, index) => (
      <SortableItem
        key={image.id || image.url}
        index={index}
        image={image}
        onDelete={onDelete}
      />
    ))}
  </div>
));

const FileInputGallery = ({ item }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preProcess, setPreProcess] = useState(false);

  useEffect(() => {
    if (item?.multipleData?.length > 0) {
      const initImages = item.multipleData.map((img, index) => ({
        id: img._id || index,
        url: img.url || img.defaultValue,
        _id: img._id || null,
      }));
      setImages(initImages);
    }
  }, [item]);


  const onDrop = useCallback(
    async (acceptedFiles) => {
      setUploading(true);
      for (let file of acceptedFiles) {
        const formData = new FormData();
        formData.append("image", file);

        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}api/v1/image-upload?process=${preProcess}`, {
            method: "POST",
            body: formData,
          });

          const result = await res.json();
          if (res.ok && result.filename) {
            const imageUrl = result.filename;
            const newImage = {
              id: crypto.randomUUID(),
              url: imageUrl,
              name: file.name,
            };
            setImages((prev) => [...prev, newImage]);
          } else {
            console.error("Upload failed:", result.error || "Unknown error");
          }
        } catch (err) {
          console.error("Upload failed", err);
          alert("Failed to upload image");
        }
      }
      setUploading(false);
    },
    [preProcess]
  );


  // const onDrop = useCallback(
  //   async (acceptedFiles) => {
  //     setUploading(true);
  //     for (let file of acceptedFiles) {
  //       const formData = new FormData();
  //       formData.append("image", file);

  //       try {
  //         const res = await fetch(`https://api.imgbb.com/1/upload?key=${imgbbKey}`, {
  //           method: "POST",
  //           body: formData,
  //         });
  //         const result = await res.json();
  //         if (result.success) {
  //           const newImage = {
  //             id: crypto.randomUUID(),
  //             url: result.data.url,
  //             name: file.name,
  //           };
  //           setImages((prev) => [...prev, newImage]);
  //         }
  //       } catch (err) {
  //         console.error("Upload failed", err);
  //         alert("Failed to upload image");
  //       }
  //     }
  //     setUploading(false);
  //   },
  //   []
  // );

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const reordered = arrayMoveImmutable(images, oldIndex, newIndex);
    setImages(reordered);
  };

  const handleDelete = (image) => {
    const updated = images.filter((img) => img.url !== image.url);
    setImages(updated);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label htmlFor="imageUpload" className="font-semibold block mb-1">{item.label}</label>
        <div className="flex items-center text-sm">
          Process
          <input
            type="checkbox"
            checked={preProcess}
            onChange={(e) => setPreProcess(e.target.checked)}
            className="ml-2 w-4 h-4 accent-white cursor-pointer"
          />
        </div>
      </div>
      <div
        {...getRootProps()}
        className="p-6 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
      >
        <input {...getInputProps()} accept="image/*" id="imageUpload" />

        {uploading ? (
          <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
            <ImSpinner2 className="w-8 h-8 text-gray-400 animate-spin" />
            <p className="text-sm font-medium">Uploading...</p>
          </div>
        ) : isDragActive ? (
          <div className="flex flex-col items-center justify-center space-y-2 text-blue-500">
            <HiArrowDownTray className="w-10 h-10" />
            <p className="text-sm font-medium">Drop the files here...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
            <HiOutlineCloudUpload className="w-10 h-10 text-gray-400" />
            <p className="text-sm">Drag & drop images here, or click to select</p>
          </div>
        )}
      </div>


      {images.length > 0 && (
        <SortableList
          images={images}
          onDelete={handleDelete}
          onSortEnd={onSortEnd}
          axis="xy"
        />
      )}
    </div>
  );
};

export default FileInputGallery;














/*

https://i.ibb.co/3mhWcdXK/service-banner-2.jpg



*/